/**
 * All logic behind "Create from Library": the library query for one form category, the two-step
 * wizard (pick flows → choose who to assign), and the copy-into-project call.
 *
 * Platform-agnostic on purpose: written to be consumed by `packages/mobile` as well as
 * `packages/web`, so it returns state, handlers and option lists only — never markup. The platform
 * passes in how to read the current selection of its own list.
 *
 * NOTE for the mobile port: this hook is the one that dispatches `getAllUsersInSpace` from
 * `@Actions/projectUsers`, which — unlike every action mobile already consumes — imports
 * `../services/axiosWrapper` by relative path instead of `@AxiosWrapper`. That relative import
 * resolves to the web axios instance (and through it the web store) on mobile too. Fix that import,
 * or inject the space-users loader the way `libraryServiceUrl` is injected, before wiring this hook
 * into a mobile screen.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { untranslated, useTranslation } from "@i18n";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { formFlowController } from "@Actions/Forms";
import { getAllUsersInSpace } from "@Actions/projectUsers";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import type { RootState } from "@Reducers/index";
import type { SpaceUserItem } from "@Reducers/projectUsers";
import type { ToastNotificationPayload } from "@Reducers/toastSlice";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import {
  buildFormFlowLibraryQuery,
  DEFAULT_USER_SELECTION_MODE,
  FORM_FLOW_LIBRARY_STEPS,
  getUserSelectionModeOptions,
  USER_SELECTION_MODES,
} from "./formFlow.constants";
import type { AddFromLibraryState, FormFlowRow } from "./formFlow.types";

interface UseFormFlowLibraryParams {
  projectId?: string;
  /** Form category whose library entries are listed. */
  resourceType: string;
  /**
   * Endpoint the library list pages against, supplied by the platform. Kept as a param rather
   * than imported here: `@Services/dataManager` builds its URLs at module-load time, which is
   * unsafe before the store holds a backend URL (notably on mobile).
   */
  libraryServiceUrl?: string;
  /** Current wizard state, owned by the caller so the list screen can open/close it. */
  addFromLibrary: AddFromLibraryState;
  setAddFromLibrary: (updater: (prev: AddFromLibraryState) => AddFromLibraryState) => void;
  /** Closes the wizard. */
  handleCancelibrary: () => void;
  /** Re-fetch the project's flows after a successful copy. */
  refreshGrid: () => void;
  /** Current selection of the library list, as row data. */
  getSelectedRows?: () => FormFlowRow[];
}

/**
 * `addFromLibrary.usersData` carries its own user shape: `name` is REQUIRED there, while the stored
 * flow-user shape produced by `toFlowUserFromNameParts` leaves `name` optional/nullable. The payload
 * row is therefore built here instead of being run through that factory and re-widened.
 */
const toLibraryUser = (user: { id: ObjectIdString; firstName?: string; lastName?: string }) => ({
  id: user.id,
  name: `${user.firstName} ${user.lastName}`,
});

function useFormFlowLibrary({
  projectId,
  resourceType,
  libraryServiceUrl,
  addFromLibrary,
  setAddFromLibrary,
  handleCancelibrary,
  refreshGrid,
  getSelectedRows,
}: UseFormFlowLibraryParams) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const spaceUsers = useSelector((state: RootState) => state.spaceUsers);
  const userDetails = useSelector((state: RootState) => state.userDetails);

  // `undefined` until the Space picker fires — there is no id for "no Space picked". The id itself
  // is typed at the picker callback, not at the request.
  const [spaceId, setSpaceId] = useState<ObjectIdString | undefined>(undefined);
  const [nextBtnState, setNextBtnState] = useState(false);
  const [selectedFlowFromLibrary, setSelectedFlowFromLibrary] = useState<FormFlowRow[]>([]);
  const [userSelectionMode, setUserSelectionMode] = useState<string>(DEFAULT_USER_SELECTION_MODE);

  const getQuery = useCallback(() => buildFormFlowLibraryQuery(resourceType), [resourceType]);
  const [customQuery, setCustomQuery] = useState<ReturnType<typeof buildFormFlowLibraryQuery>>(getQuery());

  /** Remote source the library list pages against. */
  const libraryService = libraryServiceUrl;

  useEffect(() => {
    setCustomQuery(getQuery());
  }, [getQuery]);

  // The assign-users step offers the users of the chosen space, so load them once it is picked.
  useEffect(() => {
    if (spaceId) {
      dispatch(getAllUsersInSpace({ spaceId }));
    }
  }, [spaceId, dispatch]);

  /** Copies the picked library flows into the project, assigning the chosen users. */
  const handleCreateFromLibrary = async () => {
    // `spaceId` and `projectId` are both required 24-hex ids on formFlowController's addFromLibrary
    // schema. Step 1's Next button already blocks reaching this step without a Space, and the
    // library only opens from inside a project, so there is nothing to send without either.
    const projectObjectId = objectIdString.safeParse(projectId).data;
    if (!spaceId || !projectObjectId) {
      dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
      return;
    }
    if (selectedFlowFromLibrary.length) {
      // `SpaceUserItem.id` and `UserDetails.id` are plain strings on their schemas, so the branded
      // ids the request schema wants are parsed out rather than asserted onto them.
      let usersData = spaceUsers.flatMap((user: SpaceUserItem) => {
        const id = objectIdString.safeParse(user.id).data;
        return id ? [toLibraryUser({ id, firstName: user.firstName, lastName: user.lastName })] : [];
      });

      if (userSelectionMode == USER_SELECTION_MODES.CURRENT_USER) {
        const currentUserId = objectIdString.safeParse(userDetails.id).data;
        // A signed-in user always carries an account id. Bail rather than post an empty
        // `usersData`, which would leave every copied level with no allowed users at all.
        if (!currentUserId) {
          dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
          return;
        }
        usersData = [
          toLibraryUser({ id: currentUserId, firstName: userDetails.firstName, lastName: userDetails.lastName }),
        ];
      }
      // Library rows come back through `formFlowControllerResponse`, whose `result` is
      // `z.array(z.unknown())`, so `FormFlowRow._id` is an unbranded optional string.
      const formFlowIds = selectedFlowFromLibrary.flatMap(
        (obj: FormFlowRow) => objectIdString.safeParse(obj._id).data ?? [],
      );
      // An empty list is answered with `status: true`, which would render the success toast having
      // copied nothing — so a selection that parsed down to nothing is reported as a failure.
      if (!formFlowIds.length) {
        dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
        return;
      }

      const res = await formFlowController({
        action: "addFromLibrary",
        formFlowIds,
        usersData,
        spaceId,
        projectId: projectObjectId,
      });
      // Set in both branches before the dispatch below, so no seed is needed and the type stays
      // the payload rather than a union with the seed.
      let toastContent: ToastNotificationPayload;
      if (res.data.status) {
        toastContent = {
          content: t("Selected Form Flows have been added successfully"),
          type: TOAST_TYPE_SUCCESS,
        };
        refreshGrid();
        handleCancelibrary();
      } else {
        toastContent = {
          // Raw server copy, not a translation key, so it goes through `untranslated()`.
          // `errorMessage` is optional on the failure branch of every status response, and an
          // empty `content` makes the toast reducer drop the toast entirely - hence the fallback.
          content: res.data.errorMessage ? untranslated(res.data.errorMessage) : t("Something went wrong"),
          type: TOAST_TYPE_ERROR,
        };
      }
      dispatch(showToastNotification(toastContent));
    }
  };

  /** Locks in the picked flows and moves on to the assign-users step. */
  const handleNextClick = () => {
    setSelectedFlowFromLibrary(getSelectedRows?.() ?? []);
    setAddFromLibrary((prev) => ({ ...prev, step: FORM_FLOW_LIBRARY_STEPS.ASSIGN_USERS }));
  };

  const handleBackToSelection = () => {
    setAddFromLibrary((prev) => ({ ...prev, step: FORM_FLOW_LIBRARY_STEPS.SELECT_FLOWS }));
  };

  /** Adapter for web radios that emit a `{ target: { value } }` change event. */
  const handleUserSelectionModeChange = (args: { target: { value: string } }) => {
    setUserSelectionMode(args.target.value);
  };

  const isSelectionStep = addFromLibrary.step == FORM_FLOW_LIBRARY_STEPS.SELECT_FLOWS;
  /** Next needs both a picked flow and a target space (the flow's users come from it). */
  const isNextDisabled = !nextBtnState || !spaceId;

  const userSelectionModeOptions = useMemo(() => getUserSelectionModeOptions(), []);

  return {
    // list wiring
    customQuery,
    libraryService,

    // wizard
    isSelectionStep,
    handleNextClick,
    handleBackToSelection,
    isNextDisabled,

    // space + selection
    spaceId,
    setSpaceId,
    nextBtnState,
    setNextBtnState,
    selectedFlowFromLibrary,

    // user assignment
    userSelectionMode,
    setUserSelectionMode,
    handleUserSelectionModeChange,
    userSelectionModeOptions,

    // persistence
    handleCreateFromLibrary,
  };
}

export default useFormFlowLibrary;
